import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2, X, Upload } from "lucide-react";
import { toast } from "sonner";
import { adminList, adminCreate, adminUpdate, adminDelete } from "../api";
import { ICON_MAP } from "../data";
import { T, CARD, INP } from "./theme";

const ICON_KEYS = Object.keys(ICON_MAP);
const btnGold = { background: `linear-gradient(135deg, ${T.gold}, ${T.goldHi})`, color: "#0A0A0C" };
const btnGhost = { border: `1px solid ${T.border}`, color: T.text, background: "transparent" };

function toBase64(file) {
  return new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
}

function ImageField({ value, onChange, testid }) {
  const isVideo = value && /\.(mp4|mov|webm)($|\?)/i.test(value);
  return (
    <div>
      <div className="flex gap-2 items-center">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image / video URL"
          data-testid={testid} className="flex-1 px-3 py-2 rounded-lg text-sm outline-none" style={INP} />
        <label className="px-3 py-2 rounded-lg cursor-pointer text-sm inline-flex items-center gap-1.5" style={btnGhost}>
          <Upload size={13} /> Upload
          <input type="file" accept="image/*,video/*" className="hidden"
            onChange={async (e) => { const f = e.target.files?.[0]; if (f) onChange(await toBase64(f)); }} />
        </label>
      </div>
      {value && (
        isVideo ? (
          <video src={value} className="mt-2 h-20 rounded-lg object-cover" muted playsInline preload="metadata" />
        ) : (
          <img src={value} alt="preview" className="mt-2 h-20 rounded-lg object-cover" style={{ background: T.input }} />
        )
      )}
    </div>
  );
}

function Field({ f, value, onChange }) {
  const tid = `field-${f.key}`;
  if (f.type === "textarea")
    return <textarea data-testid={tid} rows={f.rows || 3} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 rounded-lg text-sm outline-none" style={INP} />;
  if (f.type === "number")
    return <input data-testid={tid} type="number" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))}
      className="w-full px-3 py-2 rounded-lg text-sm outline-none" style={INP} />;
  if (f.type === "checkbox")
    return <input data-testid={tid} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
      className="w-4 h-4 accent-yellow-500" />;
  if (f.type === "image") return <ImageField value={value} onChange={onChange} testid={tid} />;
  if (f.type === "icon")
    return (
      <select data-testid={tid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm outline-none" style={INP}>
        <option value="">Select icon</option>
        {ICON_KEYS.map((k) => <option key={k} value={k} style={{ background: T.card }}>{k}</option>)}
      </select>
    );
  return <input data-testid={tid} value={value || ""} onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-2 rounded-lg text-sm outline-none" style={INP} />;
}

export default function CrudPanel({ coll, title, fields, labelKey = "title" }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); adminList(coll).then((d) => { setItems(d); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, [coll]);

  const startNew = () => setEditing({ __new: true });
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...editing }; delete body.__new;
      if (editing.__new) await adminCreate(coll, body);
      else await adminUpdate(coll, editing.id, body);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await adminDelete(coll, id); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid={`panel-${coll}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-display font-bold tracking-tight text-2xl md:text-3xl" style={{ color: T.text }}>{title}</h2>
        <button onClick={startNew} data-testid={`${coll}-add`}
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2 font-semibold text-sm transition-transform active:scale-[0.98]"
          style={btnGold}>
          <Plus size={16} /> Add {title.replace(/s$/, "")}
        </button>
      </div>

      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="animate-spin" style={{ color: T.gold }} /></div>
      ) : (
        <div className="grid gap-2">
          {items.map((it) => {
            const media = it.src || it.cover || it.before;
            const isVideo = media && /\.(mp4|mov|webm)($|\?)/i.test(media);
            return (
              <div key={it.id} data-testid={`${coll}-item`} className="flex items-center gap-4 p-3 rounded-xl" style={CARD}>
                {media && (isVideo ? (
                  <video src={media} className="w-14 h-14 rounded-lg object-cover" muted playsInline preload="metadata" />
                ) : (
                  <img src={media} alt="" className="w-14 h-14 rounded-lg object-cover" />
                ))}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate" style={{ color: T.text }}>{it[labelKey] || it.name || it.tag || "Untitled"}</div>
                  {(it.desc || it.excerpt) && <div className="text-xs truncate mt-0.5" style={{ color: T.textFaint }}>{it.desc || it.excerpt}</div>}
                </div>
                <button onClick={() => setEditing(it)} data-testid={`${coll}-edit`} className="px-3 py-1.5 rounded-lg text-xs" style={btnGhost}>Edit</button>
                <button onClick={() => remove(it.id)} data-testid={`${coll}-delete`} className="p-2 rounded-lg" style={{ color: T.danger }}><Trash2 size={16} /></button>
              </div>
            );
          })}
          {!items.length && (
            <div className="p-10 rounded-xl text-center" style={CARD}>
              <p style={{ color: T.textFaint }}>No {title.toLowerCase()} yet. Click &quot;Add&quot; to create one.</p>
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}>
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl p-6" style={CARD} data-testid={`${coll}-editor`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg" style={{ color: T.text }}>{editing.__new ? "New" : "Edit"} {title.replace(/s$/, "")}</h3>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg" style={btnGhost}><X size={16} /></button>
            </div>
            <div className="space-y-4">
              {fields.map((f) => (
                <div key={f.key} className={f.type === "checkbox" ? "flex items-center gap-3" : ""}>
                  <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: T.textDim }}>{f.label}</label>
                  <Field f={f} value={editing[f.key]} onChange={(v) => setEditing((p) => ({ ...p, [f.key]: v }))} />
                </div>
              ))}
            </div>
            <button onClick={save} disabled={saving} data-testid={`${coll}-save`}
              className="mt-6 w-full rounded-lg py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              style={btnGold}>
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
